import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { images } from '../mock';
import { TrendingUp, Users, Package, Target, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Services = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: TrendingUp,
      title: t.services.service1.title,
      description: t.services.service1.description,
      image: images.services[0],
      benefits: t.services.service1.benefits
    },
    {
      icon: Package,
      title: t.services.service2.title,
      description: t.services.service2.description,
      image: images.services[1],
      benefits: t.services.service2.benefits
    },
    {
      icon: Target,
      title: t.services.service3.title,
      description: t.services.service3.description,
      image: images.services[2],
      benefits: t.services.service3.benefits
    },
    {
      icon: Users,
      title: t.services.service4.title,
      description: t.services.service4.description,
      image: images.services[3],
      benefits: t.services.service4.benefits
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 min-h-[60vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/39/lIZrwvbeRuuzqOoWJUEn_Photoaday_CSD%20%281%20of%201%29-5.jpg"
            alt="Professional Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#003840]/65 via-[#003840]/55 to-[#003840]/65"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-float">
              {t.services.title}
            </h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed animate-fade-in">
              {t.services.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  className={`grid md:grid-cols-2 gap-12 items-center ${
                    isEven ? '' : 'md:grid-flow-dense'
                  }`}
                >
                  {/* Image */}
                  <div className={`relative ${isEven ? '' : 'md:col-start-2'}`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
                    />
                    <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#007369]/10 rounded-2xl -z-10"></div>
                  </div>

                  {/* Content */}
                  <div className={isEven ? '' : 'md:col-start-1 md:row-start-1'}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-4 bg-[#F4F7F6] rounded-xl">
                        <Icon className="h-8 w-8 text-[#007369]" />
                      </div>
                      <h2 className="text-3xl font-bold text-[#003840]">{service.title}</h2>
                    </div>
                    
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="space-y-3">
                      <h3 className="text-xl font-semibold text-[#003840] mb-4">{t.services.whatWeOffer}</h3>
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-[#007369] flex-shrink-0 mt-1" />
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[#F4F7F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003840] mb-4">{t.process.title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.process.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {t.process.steps.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="text-5xl font-bold text-[#007369]/20 mb-4">{String(index + 1).padStart(2, '0')}</div>
                  <h3 className="text-xl font-bold text-[#003840] mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-8 w-8 text-[#007369]/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#003840] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t.cta.services.title}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.cta.services.subtitle}
          </p>
          <a href="mailto:contact@ariamea.com">
            <Button size="lg" className="bg-[#007369] hover:bg-[#005A5B] text-white px-8 py-6 text-lg">
              {t.cta.services.button}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;